<template>
    <TransitionRoot
        as="template"
        :show="isOpen"
    >
        <Dialog
            as="div"
            class="absolute z-10"
            @close="onClose"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-backdrop transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-surface--elevated px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6"
                            :class="classObjectPanel"
                        >
                            <div
                                v-if="isClosable"
                                class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block"
                            >
                                <BaseButtonClose
                                    data-spec-class="dialog__close"
                                    @click="onClose"
                                />
                            </div>
                            <div
                                v-if="!!$slots.icon"
                                class="mx-auto mb-6 flex h-20 w-20 items-center justify-center"
                            >
                                <slot name="icon" />
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:text-left">
                                <slot />
                            </div>

                            <div
                                v-if="isClosable || !!$slots.actions"
                                class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse"
                            >
                                <slot name="actions" />
                                <BaseButton
                                    v-if="isClosable && hasMobileCancelButton"
                                    :theme="Theme.Secondary"
                                    class="w-full sm:hidden sm:w-fit"
                                    data-spec-class="dialog__cancel"
                                    @click="onClose"
                                >
                                    {{ t('actions.cancel') }}
                                </BaseButton>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, computed } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useI18n } from 'vue-i18n';

import { Size, Theme } from '@/types';
import BaseButton from './BaseButton.vue';
import BaseButtonClose from './BaseButtonClose.vue';

const { t } = useI18n();

interface Props {
    isOpen?: boolean;
    size?: Size;
    isClosable?: boolean;
    hasMobileCancelButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isOpen: false,
    size: Size.Normal,
    isClosable: true,
    hasMobileCancelButton: true,
});

const emits = defineEmits(['close']);

const classObjectPanel = computed(() => ({
    '': props.size === Size.None,
    'w-full sm:max-w-2xl': props.size === Size.Small,
    'w-full sm:max-w-4xl': props.size === Size.Normal,
    'w-full sm:max-w-8xl': props.size === Size.Large,
}));

const onClose = () => {
    if (props.isClosable) {
        emits('close');
    }
};
</script>
